import React from 'react';

const BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected || typeof document === 'undefined') return;
  stylesInjected = true;
  const el = document.createElement('style');
  el.textContent = `
    @keyframes miraMicPulse { 0% { box-shadow: 0 0 0 0 rgba(192,57,43,0.45); } 70% { box-shadow: 0 0 0 12px rgba(192,57,43,0); } 100% { box-shadow: 0 0 0 0 rgba(192,57,43,0); } }
    @keyframes miraDotBlink { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }
  `;
  document.head.appendChild(el);
}

export function useVoiceComposer({ onTranscript, onAutoSend }) {
  const [voiceState, setVoiceState] = React.useState('idle'); // idle | recording | transcribing
  const [voiceErr, setVoiceErr] = React.useState('');
  const [countdown, setCountdown] = React.useState(0);
  const recRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const maxTimerRef = React.useRef(null);
  const countRef = React.useRef(null);
  const cbRef = React.useRef({ onTranscript, onAutoSend });
  cbRef.current = { onTranscript, onAutoSend };

  const cancelAutoSend = React.useCallback(() => {
    if (countRef.current) { clearInterval(countRef.current); countRef.current = null; }
    setCountdown(0);
  }, []);

  const beginCountdown = React.useCallback((text) => {
    let n = 3;
    setCountdown(n);
    countRef.current = setInterval(() => {
      n -= 1;
      if (n <= 0) {
        clearInterval(countRef.current); countRef.current = null;
        setCountdown(0);
        cbRef.current.onAutoSend(text);
      } else setCountdown(n);
    }, 1000);
  }, []);

  const toggleRecording = React.useCallback(async () => {
    setVoiceErr('');
    if (voiceState === 'recording') {
      if (maxTimerRef.current) { clearTimeout(maxTimerRef.current); maxTimerRef.current = null; }
      try { if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop(); } catch { /* no-op */ }
      return;
    }
    if (voiceState !== 'idle') return;
    cancelAutoSend();
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setVoiceErr('Mic blocked — allow microphone access in your browser and try again.');
      return;
    }
    const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
      .find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || '';
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
    rec.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      setVoiceState('transcribing');
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
      if (blob.size < 800) {
        setVoiceState('idle');
        setVoiceErr('I barely heard anything — hold the thought a beat longer?');
        return;
      }
      const fd = new FormData();
      fd.append('file', blob, `voice.${(rec.mimeType || '').includes('mp4') ? 'mp4' : 'webm'}`);
      try {
        const r = await fetch(`${BASE}/mira/transcribe`, { method: 'POST', credentials: 'include', body: fd });
        const j = await r.json();
        if (!r.ok) throw new Error((j.detail && j.detail.message) || 'Voice slipped — try again or type it.');
        setVoiceState('idle');
        const text = (j.text || '').trim();
        if (!text) { setVoiceErr('I heard only the room — say it again?'); return; }
        cbRef.current.onTranscript(text);
        beginCountdown(text);
      } catch (e) {
        setVoiceState('idle');
        setVoiceErr(String(e.message || 'Voice slipped — try again or type it.'));
      }
    };
    rec.start();
    recRef.current = rec;
    setVoiceState('recording');
    maxTimerRef.current = setTimeout(() => {
      try { if (rec.state !== 'inactive') rec.stop(); } catch { /* no-op */ }
    }, 60000);
  }, [voiceState, cancelAutoSend, beginCountdown]);

  React.useEffect(() => () => {
    if (countRef.current) clearInterval(countRef.current);
    if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
    const rec = recRef.current;
    if (rec && rec.stream) rec.stream.getTracks().forEach((t) => t.stop());
  }, []);

  return { voiceState, voiceErr, setVoiceErr, countdown, toggleRecording, cancelAutoSend };
}

export function MicButton({ voiceState, onClick, disabled, dark, testId, size }) {
  injectStyles();
  const rec = voiceState === 'recording';
  const busy = voiceState === 'transcribing';
  const dim = size || (dark ? 40 : 38);
  const bg = rec ? '#C0392B' : busy ? '#E8B84B' : (dark ? '#35243C' : '#F5EFF7');
  const fg = rec ? '#FFFFFF' : busy ? '#3A1140' : (dark ? '#CBB8D0' : '#531B55');
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      data-testid={testId || 'mira-mic-btn'}
      aria-label={rec ? 'Stop recording' : 'Speak to Mira'}
      title={rec ? 'Tap to finish' : 'Tap and think out loud'}
      style={{
        background: bg, color: fg,
        border: dark ? 'none' : '1px solid #EFE4F0', borderRadius: 999,
        width: dim, height: dim,
        cursor: disabled || busy ? 'wait' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        animation: rec ? 'miraMicPulse 1.4s ease-out infinite' : 'none',
        transition: 'background-color 160ms ease, color 160ms ease',
      }}
    >
      {rec ? (
        <svg width="14" height="14" viewBox="0 0 14 14"><rect x="2" y="2" width="10" height="10" rx="2" fill="currentColor" /></svg>
      ) : busy ? (
        <span style={{ fontSize: 14, fontWeight: 900, animation: 'miraDotBlink 0.9s ease infinite' }}>…</span>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="22" />
        </svg>
      )}
    </button>
  );
}

export function VoiceBar({ voice, onEdit, dark, style }) {
  injectStyles();
  const { voiceState, voiceErr, setVoiceErr, countdown } = voice;
  if (voiceState === 'idle' && !voiceErr && !countdown) return null;
  const base = {
    display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700,
    borderRadius: 999, padding: '6px 14px', marginBottom: 8,
    background: dark ? '#35243C' : '#F5EFF7',
    color: dark ? '#EDE4F0' : '#531B55',
    border: dark ? '1px solid #4A3552' : '1px solid #E4D9E7',
    ...style,
  };
  if (voiceErr) {
    return (
      <div style={{ ...base, color: '#C0392B' }} data-testid="mira-voice-error">
        <span style={{ flex: 1 }}>{voiceErr}</span>
        <button onClick={() => setVoiceErr('')} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 900 }}>×</button>
      </div>
    );
  }
  if (voiceState === 'recording') {
    return (
      <div style={base} data-testid="mira-voice-recording">
        <span style={{ width: 8, height: 8, borderRadius: 99, background: '#C0392B', animation: 'miraDotBlink 1s ease infinite', flexShrink: 0 }} />
        Listening — tap the mic when you're done
      </div>
    );
  }
  if (voiceState === 'transcribing') {
    return <div style={base} data-testid="mira-voice-transcribing">✦ Catching your words…</div>;
  }
  return (
    <div style={base} data-testid="mira-voice-countdown">
      <span style={{ flex: 1 }}>Heard you — sending in {countdown}s</span>
      <button
        onClick={onEdit}
        data-testid="mira-voice-edit-btn"
        style={{
          background: dark ? '#E8B84B' : '#3A1140', color: dark ? '#3A1140' : '#F5C96A',
          border: 'none', borderRadius: 999, padding: '3px 12px', fontSize: 11,
          fontWeight: 900, cursor: 'pointer',
        }}
      >Tap to edit</button>
    </div>
  );
}
